
import { useState, useMemo } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { bookedDates } from '../../../mocks/equipmentHireCatalogue';

export default function BookingCalendar() {
  const { ref, isVisible } = useScrollAnimation();

  // Guard against unexpected data shape from the mock
  const safeBookedDates = Array.isArray(bookedDates) ? bookedDates : [];

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<string | null>(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const daysInMonth = useMemo(() => {
    return new Date(currentYear, currentMonth + 1, 0).getDate();
  }, [currentMonth, currentYear]);

  const firstDayOfMonth = useMemo(() => {
    const day = new Date(currentYear, currentMonth, 1).getDay();
    // Convert Sunday (0) to index 6 so our week starts on Monday
    return day === 0 ? 6 : day - 1;
  }, [currentMonth, currentYear]);

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const formatDate = (day: number) => {
    const m = String(currentMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${currentYear}-${m}-${d}`;
  };

  const isBooked = (day: number) => safeBookedDates.includes(formatDate(day));

  const isPast = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return date < todayStart;
  };

  const isInRange = (day: number) => {
    if (!selectedDate || !selectedEndDate) return false;
    const dateStr = formatDate(day);
    return dateStr >= selectedDate && dateStr <= selectedEndDate;
  };

  const handleDateClick = (day: number) => {
    if (isBooked(day) || isPast(day)) return;

    const dateStr = formatDate(day);

    // Start a new range
    if (!selectedDate || (selectedDate && selectedEndDate)) {
      setSelectedDate(dateStr);
      setSelectedEndDate(null);
      return;
    }

    // Complete the range
    if (dateStr < selectedDate) {
      setSelectedEndDate(selectedDate);
      setSelectedDate(dateStr);
    } else {
      setSelectedEndDate(dateStr);
    }
  };

  const formatDisplayDate = (dateStr: string) => {
    // Defensive parsing – if the string is invalid we fall back to an empty string
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const getDayCount = () => {
    if (!selectedDate || !selectedEndDate) return 0;
    const start = new Date(selectedDate + 'T00:00:00');
    const end = new Date(selectedEndDate + 'T00:00:00');
    const diff = end.getTime() - start.getTime();
    // Ensure we always return a positive integer
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1);
  };

  return (
    <section id="booking-calendar" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 mb-4">
              <i className="ri-calendar-check-line text-amber-600 text-sm"></i>
              <span className="text-amber-700 text-xs tracking-widest uppercase font-semibold">
                Availability
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-stone-900 mb-4">
              <strong>Booking Calendar</strong>
            </h2>
            <p className="text-stone-500 text-sm max-w-xl mx-auto leading-relaxed">
              Check available dates for your equipment hire. Select a start and end date to see your hire period. Greyed‑out dates are already booked.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 items-start justify-center">
            {/* Calendar */}
            <div className="w-full lg:w-[520px] bg-stone-50 rounded-lg border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={prevMonth}
                  className="w-9 h-9 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:border-amber-300 hover:text-amber-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>

                <h3 className="font-display text-xl text-stone-900">
                  {monthNames[currentMonth]} {currentYear}
                </h3>

                <button
                  onClick={nextMonth}
                  className="w-9 h-9 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:border-amber-300 hover:text-amber-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-stone-400 py-2">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells before first day */}
                {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-11"></div>
                ))}

                {/* Actual days */}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = formatDate(day);
                  const booked = isBooked(day);
                  const past = isPast(day);
                  const isStart = dateStr === selectedDate;
                  const isEnd = dateStr === selectedEndDate;
                  const inRange = isInRange(day);
                  const disabled = booked || past;

                  const baseClasses =
                    'h-11 rounded-lg text-sm font-medium transition-all whitespace-nowrap';

                  const stateClasses = disabled
                    ? booked
                      ? 'bg-stone-200/60 text-stone-300 cursor-not-allowed line-through'
                      : 'text-stone-300 cursor-not-allowed'
                    : isStart || isEnd
                    ? 'bg-amber-500 text-white shadow-md shadow-amber-500/30'
                    : inRange
                    ? 'bg-amber-100 text-amber-800'
                    : 'text-stone-700 hover:bg-amber-50 hover:text-amber-700';

                  return (
                    <button
                      key={day}
                      onClick={() => handleDateClick(day)}
                      disabled={disabled}
                      className={`${baseClasses} ${stateClasses}`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-6 mt-5 pt-4 border-t border-stone-200">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-amber-500"></div>
                  <span className="text-xs text-stone-500">Selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-amber-100"></div>
                  <span className="text-xs text-stone-500">Hire Period</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-stone-200"></div>
                  <span className="text-xs text-stone-500">Booked</span>
                </div>
              </div>
            </div>

            {/* Selection Summary */}
            <div className="w-full lg:w-80 space-y-5">
              <div className="bg-stone-50 rounded-lg border border-stone-100 p-6">
                <h4 className="font-display text-lg text-stone-900 mb-4">Your Selection</h4>

                {selectedDate ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-stone-500 uppercase tracking-wider">Start Date</span>
                      <span className="text-sm font-medium text-stone-800">
                        {formatDisplayDate(selectedDate)}
                      </span>
                    </div>

                    {selectedEndDate ? (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-stone-500 uppercase tracking-wider">End Date</span>
                          <span className="text-sm font-medium text-stone-800">
                            {formatDisplayDate(selectedEndDate)}
                          </span>
                        </div>
                        <div className="pt-3 border-t border-stone-200 flex items-center justify-between">
                          <span className="text-xs text-stone-500 uppercase tracking-wider">Duration</span>
                          <span className="text-sm font-bold text-amber-600">
                            {getDayCount()} day{getDayCount() > 1 ? 's' : ''}
                          </span>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-stone-400 italic">
                        Select an end date to complete your range
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-stone-400 italic">
                    Click a date on the calendar to start selecting your hire period
                  </p>
                )}
              </div>

              {selectedDate && selectedEndDate && (
                <a
                  href="#hire-inquiry"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-stone-900 font-semibold rounded-full hover:from-amber-300 hover:to-amber-400 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 text-sm"
                >
                  <i className="ri-mail-send-line"></i>
                  Request This Period
                </a>
              )}

              <div className="bg-amber-50 rounded-lg border border-amber-100 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-amber-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-800 mb-1">Booking Tips</p>
                    <ul className="text-xs text-stone-600 space-y-1 leading-relaxed">
                      <li>&bull; Book at least 2 weeks in advance</li>
                      <li>&bull; Weekend rates may apply</li>
                      <li>&bull; Multi‑day discounts available</li>
                      <li>&bull; Free delivery within 30km of Melbourne CBD</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
